import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { MEDIA_VARIANTS } from '../../media';
import { ImageLoader } from '../Game/ImageLoader';
import { formatBuildDuration } from '../Game/BaseCamp/baseCamp';
import {
  createBaseCampBuilding,
  getBaseCampBuildings,
  getItems,
  getItemTypes,
  getProfessions,
  handleUpload,
  updateBaseCampBuilding,
} from './AdminAPI';

const read = (value, camelName, pascalName) => (
  value?.[camelName] ?? value?.[pascalName]
);
const itemId = item => String(read(item, 'id', 'Id') || '');
const itemName = item => String(read(item, 'name', 'Name') || itemId(item));
const normalizeCosts = costs => (
  Array.isArray(costs) && costs.length > 0
    ? costs.map(cost => ({
      itemId: String(read(cost, 'itemId', 'ItemId') || ''),
      quantity: Math.max(
        1,
        Number(read(cost, 'quantity', 'Quantity')) || 1,
      ),
    }))
    : [{ itemId: '', quantity: 1 }]
);

function emptyBuilding() {
  return {
    id: '',
    key: '',
    name: '',
    description: '',
    imageId: '',
    professionKey: '',
    sortOrder: 0,
    buildDurationSeconds: 60,
    costs: [{ itemId: '', quantity: 1 }],
    upgrades: [],
    persistedUpgradeCount: 0,
    storageSlotCount: 0,
    restRegenerationMultiplier: 1,
    offersCrafting: false,
  };
}

function normalizeBuilding(value) {
  const costs = read(value, 'costs', 'Costs');
  const upgrades = read(value, 'upgrades', 'Upgrades');
  const key = String(read(value, 'key', 'Key') || '');
  return {
    id: String(read(value, 'id', 'Id') || ''),
    key,
    name: String(read(value, 'name', 'Name') || ''),
    description: String(read(value, 'description', 'Description') || ''),
    imageId: String(read(value, 'imageId', 'ImageId') || ''),
    professionKey: String(
      read(value, 'professionKey', 'ProfessionKey') || '',
    ),
    sortOrder: Number(read(value, 'sortOrder', 'SortOrder')) || 0,
    buildDurationSeconds: Math.max(
      1,
      Number(read(
        value,
        'buildDurationSeconds',
        'BuildDurationSeconds',
      )) || 1,
    ),
    costs: normalizeCosts(costs),
    upgrades: (Array.isArray(upgrades) ? upgrades : []).map((
      upgrade,
      index,
    ) => ({
      targetLevel: Math.max(
        2,
        Number(read(upgrade, 'targetLevel', 'TargetLevel')) || index + 2,
      ),
      buildDurationSeconds: Math.max(
        1,
        Number(read(
          upgrade,
          'buildDurationSeconds',
          'BuildDurationSeconds',
        )) || 1,
      ),
      costs: normalizeCosts(read(upgrade, 'costs', 'Costs')),
    })),
    persistedUpgradeCount: Array.isArray(upgrades) ? upgrades.length : 0,
    storageSlotCount: Math.max(
      0,
      Number(read(value, 'storageSlotCount', 'StorageSlotCount')) || 0,
    ),
    restRegenerationMultiplier: Math.max(
      1,
      Number(read(
        value,
        'restRegenerationMultiplier',
        'RestRegenerationMultiplier',
      )) || 1,
    ),
    offersCrafting: String(read(
      value,
      'craftingStationKey',
      'CraftingStationKey',
    ) || '') === key,
  };
}

function sortBuildings(buildings) {
  return [...buildings].sort((left, right) => (
    left.sortOrder - right.sortOrder
    || left.name.localeCompare(right.name, 'en')
    || left.key.localeCompare(right.key, 'en')
  ));
}

function buildingPayload(draft) {
  const key = draft.key.trim().toLowerCase();
  return {
    key,
    name: draft.name.trim(),
    description: draft.description.trim(),
    imageId: draft.imageId.trim(),
    professionKey: draft.offersCrafting
      ? draft.professionKey.trim()
      : '',
    sortOrder: Number.parseInt(draft.sortOrder, 10) || 0,
    buildDurationSeconds: Math.max(
      1,
      Number.parseInt(draft.buildDurationSeconds, 10) || 1,
    ),
    costs: draft.costs.map(cost => ({
      itemId: cost.itemId,
      quantity: Math.max(1, Number.parseInt(cost.quantity, 10) || 1),
    })),
    upgrades: draft.upgrades.map(upgrade => ({
      targetLevel: Number(upgrade.targetLevel),
      buildDurationSeconds: Math.max(
        1,
        Number.parseInt(upgrade.buildDurationSeconds, 10) || 1,
      ),
      costs: upgrade.costs.map(cost => ({
        itemId: cost.itemId,
        quantity: Math.max(1, Number.parseInt(cost.quantity, 10) || 1),
      })),
    })),
    storageSlotCount: Math.max(
      0,
      Number.parseInt(draft.storageSlotCount, 10) || 0,
    ),
    restRegenerationMultiplier: Math.max(
      1,
      Number.parseFloat(draft.restRegenerationMultiplier) || 1,
    ),
    craftingStationKey: draft.offersCrafting ? key : '',
  };
}

export default function AdminBaseCampBuildings() {
  const [buildings, setBuildings] = useState([]);
  const [items, setItems] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [draft, setDraft] = useState(emptyBuilding);
  const [creating, setCreating] = useState(true);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [message, setMessage] = useState('');

  const resourceItems = useMemo(() => {
    const stackableTypeIds = new Set(itemTypes
      .filter(type => Boolean(read(type, 'isStackable', 'IsStackable')))
      .map(type => String(read(type, 'id', 'Id') || '')));
    return [...items]
      .filter(item => stackableTypeIds.has(String(
        read(item, 'typeId', 'TypeId') || '',
      )))
      .sort((left, right) => itemName(left).localeCompare(
        itemName(right),
        'en',
      ));
  }, [itemTypes, items]);
  const activeProfessions = useMemo(() => [...professions]
    .filter(profession => (
      read(profession, 'isActive', 'IsActive') !== false
    ))
    .sort((left, right) => (
      (Number(read(left, 'sortOrder', 'SortOrder')) || 0)
      - (Number(read(right, 'sortOrder', 'SortOrder')) || 0)
      || String(read(left, 'name', 'Name') || '').localeCompare(
        String(read(right, 'name', 'Name') || ''),
        'en',
      )
    )), [professions]);
  const filteredBuildings = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('en');
    if (!query) return buildings;
    return buildings.filter(building => (
      `${building.name} ${building.key}`
        .toLocaleLowerCase('en')
        .includes(query)
    ));
  }, [buildings, search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setActionError('');
    setMessage('');
    try {
      const [
        buildingValues,
        itemValues,
        itemTypeValues,
        professionValues,
      ] = await Promise.all([
        getBaseCampBuildings(),
        getItems(),
        getItemTypes(),
        getProfessions(),
      ]);
      const loaded = sortBuildings(
        (buildingValues || []).map(normalizeBuilding),
      );
      const first = loaded[0] || null;
      setBuildings(loaded);
      setItems(itemValues || []);
      setItemTypes(itemTypeValues || []);
      setProfessions(professionValues || []);
      setSelectedKey(first?.key || '');
      setDraft(first || emptyBuilding());
      setCreating(!first);
    } catch (error) {
      setLoadError(
        error?.message
        || 'Camp buildings and resources could not be loaded.',
      );
      setBuildings([]);
      setItems([]);
      setItemTypes([]);
      setProfessions([]);
      setSelectedKey('');
      setDraft(emptyBuilding());
      setCreating(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectBuilding = (building) => {
    setSelectedKey(building.key);
    setDraft(building);
    setCreating(false);
    setActionError('');
    setMessage('');
  };

  const startNew = () => {
    setSelectedKey('');
    setDraft(emptyBuilding());
    setCreating(true);
    setActionError('');
    setMessage('');
  };

  const updateDraft = (field, value) => {
    setDraft(current => ({ ...current, [field]: value }));
  };

  const updateCost = (index, field, value) => {
    setDraft(current => ({
      ...current,
      costs: current.costs.map((cost, costIndex) => (
        costIndex === index ? { ...cost, [field]: value } : cost
      )),
    }));
  };

  const addCost = () => {
    setDraft(current => ({
      ...current,
      costs: [...current.costs, { itemId: '', quantity: 1 }],
    }));
  };

  const removeCost = (index) => {
    setDraft(current => ({
      ...current,
      costs: current.costs.filter((_, costIndex) => costIndex !== index),
    }));
  };

  const updateUpgrade = (upgradeIndex, field, value) => {
    setDraft(current => ({
      ...current,
      upgrades: current.upgrades.map((upgrade, index) => (
        index === upgradeIndex ? { ...upgrade, [field]: value } : upgrade
      )),
    }));
  };

  const updateUpgradeCost = (upgradeIndex, costIndex, field, value) => {
    setDraft(current => ({
      ...current,
      upgrades: current.upgrades.map((upgrade, index) => (
        index === upgradeIndex
          ? {
            ...upgrade,
            costs: upgrade.costs.map((cost, currentCostIndex) => (
              currentCostIndex === costIndex
                ? { ...cost, [field]: value }
                : cost
            )),
          }
          : upgrade
      )),
    }));
  };

  const addUpgradeCost = upgradeIndex => {
    setDraft(current => ({
      ...current,
      upgrades: current.upgrades.map((upgrade, index) => (
        index === upgradeIndex
          ? {
            ...upgrade,
            costs: [...upgrade.costs, { itemId: '', quantity: 1 }],
          }
          : upgrade
      )),
    }));
  };

  const removeUpgradeCost = (upgradeIndex, costIndex) => {
    setDraft(current => ({
      ...current,
      upgrades: current.upgrades.map((upgrade, index) => (
        index === upgradeIndex
          ? {
            ...upgrade,
            costs: upgrade.costs.filter((
              _,
              currentCostIndex,
            ) => currentCostIndex !== costIndex),
          }
          : upgrade
      )),
    }));
  };

  const addUpgrade = () => {
    setDraft(current => {
      if (current.upgrades.length >= 4) return current;
      const previous = current.upgrades[current.upgrades.length - 1] || {
        buildDurationSeconds: current.buildDurationSeconds,
        costs: current.costs,
      };
      return {
        ...current,
        upgrades: [...current.upgrades, {
          targetLevel: current.upgrades.length + 2,
          buildDurationSeconds: Math.max(
            1,
            (Number(previous.buildDurationSeconds) || 1) * 2,
          ),
          costs: previous.costs.map(cost => ({
            itemId: cost.itemId,
            quantity: Math.max(1, (Number(cost.quantity) || 1) * 2),
          })),
        }],
      };
    });
  };

  const removeLastUpgrade = () => {
    setDraft(current => (
      current.upgrades.length <= current.persistedUpgradeCount
        ? current
        : { ...current, upgrades: current.upgrades.slice(0, -1) }
    ));
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusyAction('image-upload');
    setActionError('');
    setMessage('');
    try {
      const imageId = await handleUpload(file);
      if (!imageId) throw new Error('The building image could not be uploaded.');
      updateDraft('imageId', imageId);
      setMessage('Building image uploaded. Save the building to keep it.');
    } catch (error) {
      setActionError(
        error?.message || 'The building image could not be uploaded.',
      );
    } finally {
      setBusyAction('');
      event.target.value = '';
    }
  };

  const save = async (event) => {
    event.preventDefault();
    setActionError('');
    setMessage('');
    const payload = buildingPayload(draft);
    const selectedItems = payload.costs.map(cost => cost.itemId);
    const invalidUpgrade = payload.upgrades.some(upgrade => {
      const upgradeItems = upgrade.costs.map(cost => cost.itemId);
      return upgradeItems.length === 0
        || upgradeItems.some(id => !id)
        || new Set(upgradeItems).size !== upgradeItems.length;
    });
    if (!payload.key || !payload.name) {
      setActionError('Key and name are required. Your input has been preserved.');
      return;
    }
    if (payload.costs.length === 0 || selectedItems.some(id => !id)) {
      setActionError(
        'Add at least one complete resource requirement. Your input has been preserved.',
      );
      return;
    }
    if (new Set(selectedItems).size !== selectedItems.length) {
      setActionError(
        'Each resource can only be used once. Your input has been preserved.',
      );
      return;
    }
    if (invalidUpgrade) {
      setActionError(
        'Every upgrade needs complete, unique resource requirements. '
        + 'Your input has been preserved.',
      );
      return;
    }

    setBusyAction('save');
    try {
      const value = creating
        ? await createBaseCampBuilding(payload)
        : await updateBaseCampBuilding(selectedKey, payload);
      const saved = normalizeBuilding(value || payload);
      setBuildings(current => sortBuildings([
        ...current.filter(building => building.key !== saved.key),
        saved,
      ]));
      setSelectedKey(saved.key);
      setDraft(saved);
      setCreating(false);
      setMessage(
        creating
          ? `Camp building “${saved.name}” was created.`
          : `Camp building “${saved.name}” was saved.`,
      );
    } catch (error) {
      setActionError(
        `${error?.message || 'The Camp building could not be saved.'} `
        + 'Your input has been preserved.',
      );
    } finally {
      setBusyAction('');
    }
  };

  const pageBusy = Boolean(busyAction);
  return (
    <main
      aria-busy={loading || pageBusy}
      className="admin-settingspage admin-buildings"
    >
      <aside aria-label="Camp building list" className="admin_list">
        <button
          className="button-primary"
          disabled={loading || pageBusy}
          onClick={startNew}
          type="button"
        >
          Create Camp building
        </button>
        <label>
          Filter buildings:
          <input
            onChange={event => setSearch(event.target.value)}
            placeholder="Name or key"
            type="search"
            value={search}
          />
        </label>
        {loading ? <p role="status">Loading Camp buildings…</p> : null}
        {!loading && !loadError && buildings.length === 0 ? (
          <p role="status">No Camp buildings yet.</p>
        ) : null}
        {!loading && buildings.length > 0 && filteredBuildings.length === 0 ? (
          <p role="status">No Camp buildings match the filter.</p>
        ) : null}
        {filteredBuildings.map(building => (
          <button
            aria-current={selectedKey === building.key ? 'true' : undefined}
            className={`listElement${selectedKey === building.key ? ' is-active' : ''}`}
            key={building.key}
            onClick={() => selectBuilding(building)}
            type="button"
          >
            <span>{building.name}</span>
            <small>
              {building.key} · {formatBuildDuration(
                building.buildDurationSeconds,
              )}
            </small>
          </button>
        ))}
      </aside>

      <section aria-labelledby="admin-buildings-title" className="admin_details">
        <header className="admin-professions__header">
          <div>
            <h1 id="admin-buildings-title">Camp Buildings</h1>
            <p>
              Configure construction, Camp imagery, and the crafting service
              unlocked by each project.
            </p>
          </div>
          <span className="admin-professions__count">
            {buildings.length} {buildings.length === 1 ? 'building' : 'buildings'}
          </span>
        </header>

        {loadError || actionError ? (
          <div className="admin-changelog__error" role="alert">
            <p>{actionError || loadError}</p>
            {loadError ? (
              <button onClick={loadData} type="button">Reload</button>
            ) : null}
          </div>
        ) : null}
        <p
          aria-live="polite"
          className="admin-professions__message"
          role="status"
        >
          {message}
        </p>

        {loading ? (
          <p role="status">Loading buildings, resources, and professions…</p>
        ) : !loadError ? (
          <form className="admin-professions__form" onSubmit={save}>
            <header>
              <h2>{creating ? 'Create Camp building' : `Edit ${draft.name}`}</h2>
              <span>{formatBuildDuration(draft.buildDurationSeconds)}</span>
            </header>

            <div className="admin-professions__form-grid">
              <label>
                Key:
                <input
                  disabled={!creating}
                  onChange={event => updateDraft('key', event.target.value)}
                  pattern="[a-z][a-z0-9_]{1,63}"
                  placeholder="alchemy_lab"
                  required
                  value={draft.key}
                />
                <small>Stable lowercase ID; cannot be changed later.</small>
              </label>
              <label>
                Name:
                <input
                  onChange={event => updateDraft('name', event.target.value)}
                  required
                  value={draft.name}
                />
              </label>
              <label className="admin-professions__wide">
                Description:
                <textarea
                  onChange={event => updateDraft(
                    'description',
                    event.target.value,
                  )}
                  rows="3"
                  value={draft.description}
                />
              </label>
              <label>
                Build time in seconds:
                <input
                  min="1"
                  onChange={event => updateDraft(
                    'buildDurationSeconds',
                    event.target.value,
                  )}
                  required
                  step="1"
                  type="number"
                  value={draft.buildDurationSeconds}
                />
              </label>
              <label>
                Sort order:
                <input
                  min="0"
                  onChange={event => updateDraft(
                    'sortOrder',
                    event.target.value,
                  )}
                  step="1"
                  type="number"
                  value={draft.sortOrder}
                />
              </label>
            </div>

            <fieldset className="admin-progression-fieldset">
              <legend>Required resources</legend>
              <div className="admin-buildings__costs">
                {draft.costs.map((cost, index) => (
                  <div
                    className="admin-buildings__cost"
                    key={`${index}-${cost.itemId}`}
                  >
                    <label>
                      Resource:
                      <select
                        aria-label={`Resource ${index + 1}`}
                        onChange={event => updateCost(
                          index,
                          'itemId',
                          event.target.value,
                        )}
                        required
                        value={cost.itemId}
                      >
                        <option value="">Select an item</option>
                        {resourceItems.map(item => (
                          <option key={itemId(item)} value={itemId(item)}>
                            {itemName(item)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Quantity:
                      <input
                        aria-label={`Resource ${index + 1} quantity`}
                        min="1"
                        onChange={event => updateCost(
                          index,
                          'quantity',
                          event.target.value,
                        )}
                        required
                        step="1"
                        type="number"
                        value={cost.quantity}
                      />
                    </label>
                    <button
                      disabled={draft.costs.length === 1}
                      onClick={() => removeCost(index)}
                      type="button"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              <button onClick={addCost} type="button">Add resource</button>
            </fieldset>

            <fieldset className="admin-progression-fieldset">
              <legend>Level upgrades</legend>
              <p>
                Existing levels are immutable. Add consecutive levels up to
                level 5; new levels copy and double the previous time and costs.
              </p>
              <div className="admin-buildings__costs">
                {draft.upgrades.map((upgrade, upgradeIndex) => {
                  const immutable = (
                    !creating
                    && upgradeIndex < draft.persistedUpgradeCount
                  );
                  return (
                    <section
                      aria-labelledby={`building-upgrade-${upgrade.targetLevel}`}
                      className="admin-progression-fieldset"
                      key={upgrade.targetLevel}
                    >
                      <h3 id={`building-upgrade-${upgrade.targetLevel}`}>
                        Level {upgrade.targetLevel}
                      </h3>
                      {immutable ? (
                        <p>Published level — values cannot be changed.</p>
                      ) : null}
                      <label>
                        Level {upgrade.targetLevel} build time in seconds:
                        <input
                          disabled={immutable}
                          min="1"
                          onChange={event => updateUpgrade(
                            upgradeIndex,
                            'buildDurationSeconds',
                            event.target.value,
                          )}
                          required
                          step="1"
                          type="number"
                          value={upgrade.buildDurationSeconds}
                        />
                      </label>
                      {upgrade.costs.map((cost, costIndex) => (
                        <div
                          className="admin-buildings__cost"
                          key={`${upgrade.targetLevel}-${costIndex}-${cost.itemId}`}
                        >
                          <label>
                            Resource:
                            <select
                              aria-label={`Level ${upgrade.targetLevel} resource ${costIndex + 1}`}
                              disabled={immutable}
                              onChange={event => updateUpgradeCost(
                                upgradeIndex,
                                costIndex,
                                'itemId',
                                event.target.value,
                              )}
                              required
                              value={cost.itemId}
                            >
                              <option value="">Select an item</option>
                              {resourceItems.map(item => (
                                <option
                                  key={itemId(item)}
                                  value={itemId(item)}
                                >
                                  {itemName(item)}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Quantity:
                            <input
                              aria-label={`Level ${upgrade.targetLevel} resource ${costIndex + 1} quantity`}
                              disabled={immutable}
                              min="1"
                              onChange={event => updateUpgradeCost(
                                upgradeIndex,
                                costIndex,
                                'quantity',
                                event.target.value,
                              )}
                              required
                              step="1"
                              type="number"
                              value={cost.quantity}
                            />
                          </label>
                          <button
                            disabled={immutable || upgrade.costs.length === 1}
                            onClick={() => removeUpgradeCost(
                              upgradeIndex,
                              costIndex,
                            )}
                            type="button"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        disabled={immutable}
                        onClick={() => addUpgradeCost(upgradeIndex)}
                        type="button"
                      >
                        Add level {upgrade.targetLevel} resource
                      </button>
                    </section>
                  );
                })}
              </div>
              <div className="admin-professions__actions">
                <button
                  disabled={draft.upgrades.length >= 4}
                  onClick={addUpgrade}
                  type="button"
                >
                  Add level {draft.upgrades.length + 2}
                </button>
                <button
                  disabled={
                    draft.upgrades.length <= draft.persistedUpgradeCount
                  }
                  onClick={removeLastUpgrade}
                  type="button"
                >
                  Remove last unpublished level
                </button>
              </div>
            </fieldset>

            <fieldset className="admin-progression-fieldset">
              <legend>Camp service</legend>
              <div className="admin-professions__form-grid">
                <label className="admin-professions__check">
                  <input
                    checked={draft.offersCrafting}
                    onChange={event => updateDraft(
                      'offersCrafting',
                      event.target.checked,
                    )}
                    type="checkbox"
                  />
                  Opens a crafting service
                </label>
                <label>
                  Profession filter:
                  <select
                    disabled={!draft.offersCrafting}
                    onChange={event => updateDraft(
                      'professionKey',
                      event.target.value,
                    )}
                    value={draft.professionKey}
                  >
                    <option value="">All professions assigned to this station</option>
                    {activeProfessions.map(profession => {
                      const key = String(
                        read(profession, 'key', 'Key') || '',
                      );
                      return (
                        <option key={key} value={key}>
                          {String(read(profession, 'name', 'Name') || key)}
                        </option>
                      );
                    })}
                  </select>
                  <small>
                    Recipes must also select this building as their station in
                    the Professions area.
                  </small>
                </label>
                <label>
                  Storage slots:
                  <input
                    min="0"
                    onChange={event => updateDraft(
                      'storageSlotCount',
                      event.target.value,
                    )}
                    step="1"
                    type="number"
                    value={draft.storageSlotCount}
                  />
                </label>
                <label>
                  Rest regeneration multiplier:
                  <input
                    min="1"
                    onChange={event => updateDraft(
                      'restRegenerationMultiplier',
                      event.target.value,
                    )}
                    step="0.1"
                    type="number"
                    value={draft.restRegenerationMultiplier}
                  />
                </label>
              </div>
            </fieldset>

            <fieldset className="admin-progression-fieldset">
              <legend>Camp image</legend>
              <label>
                Upload image:
                <input
                  accept="image/*"
                  disabled={pageBusy}
                  onChange={uploadImage}
                  type="file"
                />
              </label>
              {draft.imageId ? (
                <div className="admin-buildings__image">
                  <ImageLoader
                    alt={`${draft.name || 'Camp building'} preview`}
                    imageId={draft.imageId}
                    variant={MEDIA_VARIANTS.CARD}
                  />
                  <button
                    disabled={pageBusy}
                    onClick={() => updateDraft('imageId', '')}
                    type="button"
                  >
                    Remove image
                  </button>
                </div>
              ) : (
                <p className="admin-professions__empty">
                  No image assigned. The Camp uses its default landmark icon.
                </p>
              )}
            </fieldset>

            <div className="admin-professions__actions">
              <button
                className="button-primary"
                disabled={pageBusy}
                type="submit"
              >
                {busyAction === 'save'
                  ? 'Saving…'
                  : creating ? 'Create building' : 'Save changes'}
              </button>
            </div>
          </form>
        ) : null}
      </section>
    </main>
  );
}
